import { useState, useEffect, useCallback } from 'react';

import { Preloader } from '@/src/ui';
import { AuthContext, type UserInfo } from './useAuth';
import { apiRequest, AUTH_EXPIRED_EVENT } from '@/src/services';
import { Constants } from '@/src/utils';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper to merge API data with LocalStorage preferences
    const mergeUserWithStorage = useCallback((userData: UserInfo): UserInfo => {
        try {
            const storageKey = `grade_theme_override_${userData.id}`;
            const storedOverride = localStorage.getItem(storageKey);

            // If the incoming user data doesn't have an override, but LocalStorage does, use Storage.
            // This handles page refreshes and initial logins.
            if (!userData.alternateGradeColorShortCode && storedOverride) {
                return {
                    ...userData,
                    alternateGradeColorShortCode: storedOverride,
                };
            }
            return userData;
        } catch {
            return userData;
        }
    }, []);
    const getUserMinFontSize = useCallback((gradeShortCode: string): number => {
        switch (gradeShortCode) {
            case 'TK':
                return Constants.GRADE_MIN_FONT_SIZES['TK'];
            case 'K':
                return Constants.GRADE_MIN_FONT_SIZES['K'];
            case '1':
                return Constants.GRADE_MIN_FONT_SIZES['1'];
            case '2':
                return Constants.GRADE_MIN_FONT_SIZES['2'];
            default:
                return Constants.GRADE_MIN_FONT_SIZES['default'];
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const res = await apiRequest<UserInfo>('get', 'getUserDetails');

                // Merge API result with LocalStorage before setting state
                const userWithTheme = mergeUserWithStorage(res.data);
                userWithTheme.minFontSize = getUserMinFontSize(userWithTheme.gradeShortCode);

                setUser(userWithTheme);
            } catch {
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        checkAuth();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = (userData: UserInfo) => {
        // Also merge on explicit login actions
        const userWithTheme = mergeUserWithStorage(userData);
        setUser(userWithTheme);
    };

    const logout = useCallback(async (shouldPost: boolean) => {
        if (shouldPost) {
            apiRequest('post', 'auth/logout');
            localStorage.setItem('hasLoggedOut', 'true');
            setUser(null);
        } else {
            setUser(null);
        }
    }, []);

    useEffect(() => {
        const handleAuthExpired = () => {
            setUser(null);
        };
        window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
        return () => {
            window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
        };
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, login, logout, loading }}>
            {!loading ? children : <Preloader />}
        </AuthContext.Provider>
    );
};
